import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal";
import { toast } from "sonner"
import Button from "../Common/Button"
import { useTranslation } from '../../utils/translations';

const { FiSave, FiX, FiPlus, FiTrash2, FiImage } = FiIcons;

const WidgetForm = ({ widget = null, agents = [], onCancel, onSubmit, isEdit = false }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [loading, setLoading] = useState(false);
  const [origins, setOrigins] = useState(['']);
  const [formData, setFormData] = useState({
    name: '',
    agent_id: '',
    is_active: true,
    ui: {
      primaryColor: '#854fff',
      position: 'bottom-right',
      welcomeMessage: t('widgets.widgetForm.welcomeMessageDefault', 'How can I help you today?'),
      agentName: t('widgets.widgetForm.agentNameDefault', 'AI Assistant'),
      agentImage: 'https://seo-local.ca/wp-content/uploads/2025/05/shutterstock_2591333427-scaled.jpg',
      autoOpenDelay: 15000,
      firstMessage: t('widgets.widgetForm.firstMessageDefault', 'I am an AI agent to help you find what you are looking for!'),
      language: 'fr'
    },
    integration: {
      type: 'agent',
      webhook: {
        url: '',
        route: 'general'
      }
    }
  });

  // Initialize form with widget data if editing
  useEffect(() => {
    if (isEdit && widget) {
      setFormData({
        ...widget,
        ui: {
          primaryColor: widget.ui?.primaryColor || '#854fff',
          position: widget.ui?.position || 'bottom-right',
          welcomeMessage: widget.ui?.welcomeMessage || t('widgets.widgetForm.welcomeMessageDefault', 'How can I help you today?'),
          agentName: widget.ui?.agentName || t('widgets.widgetForm.agentNameDefault', 'AI Assistant'),
          agentImage: widget.ui?.agentImage || 'https://seo-local.ca/wp-content/uploads/2025/05/shutterstock_2591333427-scaled.jpg',
          autoOpenDelay: widget.ui?.autoOpenDelay || 15000,
          firstMessage: widget.ui?.firstMessage || t('widgets.widgetForm.firstMessageDefault', 'I am an AI agent to help you find what you are looking for!'),
          language: widget.ui?.language || 'fr'
        },
        integration: widget.integration || {
          type: 'agent',
          webhook: {
            url: '',
            route: 'general'
          }
        }
      });

      // Set origins from widget
      if (widget.allowed_origins && widget.allowed_origins.length > 0) {
        setOrigins(widget.allowed_origins);
      } else {
        setOrigins(['']);
      }

      // If webhook integration, update type
      if (widget.integration?.webhook?.url) {
        setFormData(prev => ({
          ...prev,
          integration: {
            ...prev.integration,
            type: 'webhook'
          }
        }));
      }
    }
  }, [isEdit, widget]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleUIChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      ui: { ...formData.ui, [name]: value }
    });
  };

  const handleAutoOpenDelayChange = (e) => {
    const value = parseInt(e.target.value) * 1000; // Convert seconds to milliseconds
    setFormData({
      ...formData,
      ui: { ...formData.ui, autoOpenDelay: value }
    });
  };

  const handleIntegrationTypeChange = (e) => {
    const type = e.target.value;
    setFormData({
      ...formData,
      integration: { ...formData.integration, type }
    });
  };

  const handleWebhookChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      integration: {
        ...formData.integration,
        webhook: { ...formData.integration.webhook, [name]: value }
      }
    });
  };

  const handleAddOrigin = () => {
    setOrigins([...origins, '']);
  };

  const handleRemoveOrigin = (index) => {
    const newOrigins = [...origins];
    newOrigins.splice(index, 1);
    setOrigins(newOrigins.length > 0 ? newOrigins : ['']);
  };

  const handleOriginChange = (index, value) => {
    const newOrigins = [...origins];
    newOrigins[index] = value;
    setOrigins(newOrigins);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);    

    try {
      // Filter out empty origins
      const filteredOrigins = origins.filter(origin => origin.trim() !== '');

      // Prepare data for submission
      const widgetData = {
        name: formData.name,
        is_active: formData.is_active,
        ui: formData.ui,
        integration: formData.integration,
        allowed_origins: filteredOrigins.length > 0 ? filteredOrigins : null
      };

      // Set agent_id only if integration type is 'agent' and a valid agent is selected
      if (formData.integration.type === 'agent') {
        if (!formData.agent_id) {
          throw new Error(t('widgets.widgetForm.selectAgentError', 'Please select an agent'));
        }
        widgetData.agent_id = formData.agent_id;
      } else {
        widgetData.agent_id = null;
      }

      if (isEdit) {
        // Update existing widget
        const { data, error } = await supabase
          .from('widgets_a1b2c3d4e5')
          .update({
            ...widgetData,
            updated_at: new Date().toISOString()
          })
          .eq('id', widget.id)
          .select();

        if (error) throw error;
        if (data && data.length > 0) {
          onSubmit(data[0], true);
          toast.success(t('widgets.widgetForm.updateSuccess', 'Widget updated successfully'));
        }
      } else {
        // Create new widget
        const { data, error } = await supabase
          .from('widgets_a1b2c3d4e5')
          .insert({
            ...widgetData,
            created_by: user.id,
            organization_id: user.organizationId // Assuming user object has organizationId
          })
          .select();

        if (error) throw error;
        if (data && data.length > 0) {
          onSubmit(data[0], false);
          toast.success(t('widgets.widgetForm.createSuccess', 'Widget created successfully'));
        }
      }
    } catch (error) {
      console.error('Error saving widget:', error);
      toast.error(t('widgets.widgetForm.saveError', 'Failed to save widget.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} title={isEdit ? t('widgets.widgetForm.editWidgetTitle', 'Edit Widget') : t('widgets.widgetForm.createWidgetTitle', 'Create New Widget')} onClose={onCancel} size="max-w-full">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              {t('widgets.widgetForm.basicSettings', 'Basic Settings')}
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.widgetName', 'Widget Name *')}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder={t('widgets.widgetForm.widgetNamePlaceholder', 'Customer Support Chat')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.integrationType', 'Integration Type')}
              </label>
              <select
                name="integrationType"
                value={formData.integration.type}
                onChange={handleIntegrationTypeChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="agent">{t('widgets.widgetForm.aiAgent', 'AI Agent')}</option>
                <option value="webhook">{t('widgets.widgetForm.customWebhook', 'Custom Webhook')}</option>
              </select>
            </div>

            {formData.integration.type === 'agent' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('widgets.widgetForm.aiAgent', 'AI Agent *')}
                </label>
                <select
                  name="agent_id"
                  value={formData.agent_id}
                  onChange={handleChange}
                  required={formData.integration.type === 'agent'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('widgets.widgetForm.selectAgent', 'Select an agent')}</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('widgets.widgetForm.webhookUrl', 'Webhook URL *')}
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={formData.integration.webhook.url}
                    onChange={handleWebhookChange}
                    required={formData.integration.type === 'webhook'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('widgets.widgetForm.webhookUrlPlaceholder', 'https://your-webhook-endpoint.com')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('widgets.widgetForm.webhookRoute', 'Webhook Route')}
                  </label>
                  <input
                    type="text"
                    name="route"
                    value={formData.integration.webhook.route}
                    onChange={handleWebhookChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('widgets.widgetForm.webhookRoutePlaceholder', 'general')}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-xs"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('widgets.widgetForm.isActive', 'Active')}</span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('widgets.widgetForm.isActiveDescription', 'When inactive, the widget won\'t be accessible on any website.')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('widgets.widgetForm.allowedOrigins', 'Allowed Origins')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('widgets.widgetForm.allowedOriginsDescription', 'Specify which websites can use this widget. Leave empty to allow all origins.')}
              </p>
              <div className="space-y-2">
                {origins.map((origin, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={origin}
                      onChange={(e) => handleOriginChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t('widgets.widgetForm.originPlaceholder', 'https://example.com')}
                    />
                    <Button
                      onClick={() => handleRemoveOrigin(index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                      icon={FiTrash2}
                      title={t('widgets.widgetForm.removeOrigin', 'Remove Origin')}
                    />                     
                  </div>
                ))}
                <Button
                  onClick={handleAddOrigin}
                  className="flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  icon={FiPlus}
                  iconClassName="w-4 h-4"
                >
                  {t('widgets.widgetForm.addOrigin', 'Add Origin')}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              {t('widgets.widgetForm.appearance', 'Appearance')}
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('widgets.widgetForm.agentName', 'Agent Name')}
                </label>
                <input
                  type="text"
                  name="agentName"
                  value={formData.ui.agentName}
                  onChange={handleUIChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('widgets.widgetForm.position', 'Position')}
                </label>
                <select
                  name="position"
                  value={formData.ui.position}
                  onChange={handleUIChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="bottom-right">{t('widgets.widgetForm.positionBottomRight', 'Bottom Right')}</option>
                  <option value="bottom-left">{t('widgets.widgetForm.positionBottomLeft', 'Bottom Left')}</option>
                  <option value="top-right">{t('widgets.widgetForm.positionTopRight', 'Top Right')}</option>
                  <option value="top-left">{t('widgets.widgetForm.positionTopLeft', 'Top Left')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('widgets.widgetForm.primaryColor', 'Primary Color')}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.ui.primaryColor}
                    onChange={(e) => handleUIChange({ target: { name: 'primaryColor', value: e.target.value } })}
                    className="h-9 w-9 border-0 p-0 rounded-xs"
                  />
                  <input
                    type="text"
                    name="primaryColor"
                    value={formData.ui.primaryColor}
                    onChange={handleUIChange}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('widgets.widgetForm.autoOpenDelay', 'Auto-Open Delay (seconds)')}
                </label>
                <input
                  type="number"
                  value={formData.ui.autoOpenDelay / 1000}
                  onChange={handleAutoOpenDelayChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('widgets.widgetForm.autoOpenDelayDescription', 'Time in seconds before the chat automatically opens. Set to 0 to disable.')}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.agentImageUrl', 'Agent Image URL')}
              </label>
              <div className="flex space-x-3">
                <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600">
                  {formData.ui.agentImage ? (
                    <img
                      src={formData.ui.agentImage}
                      alt={t('widgets.widgetForm.agentImageAlt', 'Agent')}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                        e.target.classList.add('flex', 'items-center', 'justify-center', 'bg-gray-200');
                        const icon = document.createElement('div');
                        icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                        e.target.parentNode.appendChild(icon);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                      <SafeIcon icon={FiImage} className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  name="agentImage"
                  value={formData.ui.agentImage}
                  onChange={handleUIChange}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('widgets.widgetForm.agentImagePlaceholder', 'https://example.com/image.jpg')}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('widgets.widgetForm.agentImageDescription', 'URL to the agent\'s avatar image (recommended size: 200x200px)')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.welcomeMessage', 'Welcome Message')}
              </label>
              <input
                type="text"
                name="welcomeMessage"
                value={formData.ui.welcomeMessage}
                onChange={handleUIChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.firstMessage', 'First Message')}
              </label>
              <input
                type="text"
                name="firstMessage"
                value={formData.ui.firstMessage}
                onChange={handleUIChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('widgets.widgetForm.language', 'Language')}
              </label>
              <select
                name="language"
                value={formData.ui.language}
                onChange={handleUIChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="fr">{t('widgets.widgetForm.french', 'French')}</option>
                <option value="en">{t('widgets.widgetForm.english', 'English')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <Button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('widgets.widgetForm.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            loading={loading}
            icon={FiSave}
          >
            {isEdit ? t('widgets.widgetForm.updateWidget', 'Update Widget') : t('widgets.widgetForm.createWidget', 'Create Widget')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default WidgetForm;